import * as React from 'react';

import { Slug } from '@fnv-core/utils';
import { FormControlProps } from '../interfaces/FormControlProps';
import { FormStylingHelpers } from './helpers/FormStylingHelpers';
import { FormLabelHelpers } from './helpers/FormLabelHelpers';
import { PropertyHelpers } from '../helpers/PropertyHelpers';
import { FormControlHelpers } from './helpers/FormControlHelpers';
import ReactHtmlParser from 'react-html-parser';
import { FileNameHelpers } from '../helpers/FileNameHelpers';
import { LabelAndPopover } from './components/label-and-popover';

export interface FileUploadState {
    dataSourceCounter: number;
    timer: any;
    showPopoverText : boolean;
    fileName: string;
    hasFocus: boolean;
    resetFocus: boolean;
}

class FileUpload extends React.Component<FormControlProps, FileUploadState> {
    constructor(props: FormControlProps) {
        super(props);
        this.fileUploadInputRef = React.createRef();
        this.state = {
            dataSourceCounter: 0,
            timer: undefined,
            showPopoverText: false,
            fileName: "",
            hasFocus: false,
            resetFocus: false
        };
    }

    fileUploadInputRef = undefined;
    unmounted = false;

    componentDidMount() {
        
        let inputProperties = {
            // Input properties has everything needed to deal with manual input
            // It will load related data when a datasource is set
            ...FormControlHelpers.inputProperties(this)
        };

        // restore uploaded file
        if (this.props.field.value) {            
            if (typeof this.props.field.value === "string") {
                let fileName = this.props.field.value || "";
                this.setState({fileName : fileName}, () => {
                    this.props.formikBag.setFieldValue(this.props.formFieldDefinition.name, fileName);
                });
            } else
            if (this.props.field.value && this.props.field.value.name) {
                let fileName = this.props.field.value.name || "";
                this.setState({fileName : fileName}, () => {
                    this.props.formikBag.setFieldValue(this.props.formFieldDefinition.name, this.props.field.value);
                });
            }
        } else
        if (inputProperties.value && (inputProperties.value as any).name) {
            this.setState({fileName : (inputProperties.value as any).name}, () => {
                this.props.formikBag.setFieldValue(this.props.formFieldDefinition.name, inputProperties.value);
            });
        } else {
            if (this.fileUploadInputRef) {  
                this.fileUploadInputRef.value = null;
            }
            this.setState({fileName : ""}, () => {
                this.props.formikBag.setFieldValue(this.props.formFieldDefinition.name, "");
            });
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.removeFocusEventHandler();
    }

    componentDidUpdate() {        
        let resetFocusHelper : any = {};
        if (this.state.resetFocus) {
            resetFocusHelper.hasFocus = false;
            resetFocusHelper.resetFocus = false;
        }

        if (typeof this.props.field.value === "string" && this.props.field.value !== this.state.fileName) {
            let fileName = this.props.field.value || "";        
            this.setState({...resetFocusHelper, fileName : fileName});
        } else
        if (this.props.field.value && this.props.field.value.name && this.props.field.value.name !== this.state.fileName) {
            let fileName = this.props.field.value.name || "";
            this.setState({...resetFocusHelper, fileName : fileName});
        } else if (!!this.state.resetFocus) {
            this.setState({...resetFocusHelper});
        }
    }

    showPopoverText = (event) => {
        event.preventDefault();
        this.setState({showPopoverText: !this.state.showPopoverText});
        return false;
    }

    removeFile = (event) => {
        event.preventDefault();
        this.removeFocusEventHandler();
        this.setState({fileName: "", hasFocus: false, resetFocus: false}, () => {
            if (this.fileUploadInputRef) {
                this.fileUploadInputRef.value = null;
            }
            this.props.formikBag.setFieldValue(this.props.formFieldDefinition.name, "");            
        });        
        return false;
    }

    onInputFocus = (event) => {   
        this.setState({hasFocus : true});
    }

    onInputClick = (event) => {          
        this.resetFocusWasRequestedFromBodyFocus = false; 
        this.setState({hasFocus : true});
        document.body.addEventListener("focus" , this.onBodyFocus, true);
    }   
    
    timeoutOnBodyFocus : any = undefined;
    resetFocusWasRequestedFromBodyFocus: boolean = false;

    onBodyFocus = (event) => {
        this.resetFocusWasRequestedFromBodyFocus = true;

        // hacky construct, but we need workaround to catch "cancel filedialog", since there's no out of the box browser event for this
        // the onFocus on the document.body is set up before the filedialog is opened
        // It is also triggered in the onChange of the file input (onChange is not triggered when the filedialog is canceled)
        // onFocus on the body is fired before the onChange event fires, so we need a way to circumvent this. 
        // In the small chance that the timeout is too short, then the user will see an error message flashing that no file is selected.
        // This code tries to prevent that as good as it can
        this.timeoutOnBodyFocus = setTimeout(() => {
            if (!this.unmounted) {    
                this.resetFocusWasRequestedFromBodyFocus = false;
                this.setState({resetFocus : true});
            }
        }, 750); 

        document.body.removeEventListener("focus" , this.onBodyFocus, true);
    }

    checkNeedsFocusReset = () => {
        if (this.resetFocusWasRequestedFromBodyFocus) {
            this.resetFocus();
        }
    }

    resetFocus = () => {
        this.setState({hasFocus : false});
    }

    removeFocusEventHandler = () => {
        this.resetFocusWasRequestedFromBodyFocus = false;
        document.body.removeEventListener("focus" , this.onBodyFocus, true);
        if (this.timeoutOnBodyFocus) {
            clearTimeout(this.timeoutOnBodyFocus);
            this.timeoutOnBodyFocus = undefined;
        }
    }

    onInputChange = (event) => {
        this.checkNeedsFocusReset();
        this.removeFocusEventHandler();
        if (!event.target.files) {
            this.setState({
                hasFocus: false
            });
            return false;
        }
        this.setState({
            fileName: event.currentTarget.files[0].name, 
            hasFocus: false
        });
        return true;
    }

    render() {
        let inputProperties = {
            // Input properties has everything needed to deal with manual input
            // It will load related data when a datasource is set
            ...FormControlHelpers.inputProperties(this)
        };        

        // remove value property to prevent errors specific to file-upload dom input element
        // (it causes javascript errors when uploading file)
        delete inputProperties.value;

        const fileNameExtensionImage = FileNameHelpers.getFileAttachmentImage(this.state.fileName);
        const fileNameExtension = FileNameHelpers.getFileNameExtensionUppercase(this.state.fileName);
        return (
            <>
            <div className={FormStylingHelpers.getFormGroupStyle(this.props) + ' ' +
                'file-upload-form-control ' +
                'form__section-input ' + 
                Slug.fromString(this.props.formFieldDefinition.name) + '-input-form-group ' +
                (this.state.fileName === "" ? "" : "file-upload-form-control--has-file") 
                }                
                
                >
                {this.state.fileName === "" && <LabelAndPopover 
                        className="btn btn-primary"
                        fieldId={PropertyHelpers.getFieldID(this.props)} 
                        label={FormLabelHelpers.getFieldLabel(this)}
                        explanation={this.props.formFieldDefinition.explanation}
                        hasPopover={this.props.formFieldDefinition.hasPopover}                        
                        containerClassName="label-and-popover__fileupload-container"                    
                    ></LabelAndPopover>}
                <div className="input-group">
                    <input
                        {...inputProperties}
                        ref={ref => {this.fileUploadInputRef = ref; } }
                        maxLength={PropertyHelpers.getMaxLength(this.props, 250)}
                        type="file"
                        className={FormStylingHelpers.getInputStyle(this)}
                        onClick={this.onInputClick}                                           
                    />
                    <div className="input-group-append">
                        {fileNameExtensionImage !== "" && <span className="filename-ext-icon">
                            <span className="filename-ext-icon__text">{fileNameExtension}</span>
                            <img className="filename-ext-icon__image" src={fileNameExtensionImage} />
                        </span>}
                        <span>{this.state.fileName}</span>
                        {this.state.fileName !== "" && <span onClick={this.removeFile} className="icon icon-trash"></span>}
                    </div>
                </div>
                {this.props.formFieldDefinition.hasPopover === undefined && 
                 this.props.formFieldDefinition.explanation && 
                    this.props.formFieldDefinition.explanation !== "" ? <span className="form-control-plaintext-explanation">{ReactHtmlParser(this.props.formFieldDefinition.explanation)}</span> : null}
            </div>
            {!this.state.hasFocus && FormControlHelpers.invalidFeedbackComponent(this)}
            <div className={ Slug.fromString(this.props.formFieldDefinition.name) + "-input-form-group-after" + " form-group-after "}></div>
            </>
        );
    }
}

export { FileUpload };
